// src/app/service/Module.service.ts
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { BehaviorSubject, Observable, defer, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UzorService } from '../encryption/uzor.service';

/* ---------------------------------------------------------------------
   Types – exactly the same shape the backend returns
   --------------------------------------------------------------------- */
export interface ServiceRouteMap {
  get?: string | string[];
  post?: string | string[];
  put?: string | string[];
  patch?: string | string[];
  delete?: string | string[];
  [method: string]: string | string[] | undefined;
}
export interface ServiceApiMap {
  [serviceKey: string]: ServiceRouteMap;
}
export interface ModuleInfo {
  name: string;
  ip: string | null;
  port: number | null;
  type: string | null;
  serverUrl: string | null;
  apiEndpoint: string | null;
  initialized: boolean;
  built: boolean;
  hasBackendEnv?: boolean;
  hasFrontendEnv?: boolean;
  isLoadable: boolean;
  dailyHash?: string | null;
  dailyHashShort?: string | null;
  dateGenerated?: string | null;
  clientPath?: string | null;
  serverPath?: string | null;
  publicPath?: string | null;
  note?: string;
  publicservices?: string[];
  api?: ServiceApiMap;
}
interface ModuleListResponse {
  success: boolean;
  modules?: ModuleInfo[];
  module?: ModuleInfo;
  error?: string;
  code?: string;
  receivedRequestAt: string;
  processingTimeMs: number;
  data: any;
  _cookies?: Record<string, any>;
}
export interface ModuleCallResponse<T = any> {
  success: boolean;
  error?: string;
  /** optional machine-readable reason, e.g. 'KEY_INVALID' (see isKeyRejection) */
  code?: string;
  receivedRequestAt: string;
  processingTimeMs: number;
  data: T | null;
  /** set by the client on synthetic (never reached the server) responses – ignored for clock sync */
  _local?: boolean;
  information?: Record<string, { publicservices: string[]; api: ServiceApiMap }>;
  _cookies?: Record<string, any>;
}
export type HttpVerb = 'get' | 'post' | 'put' | 'patch' | 'delete';

@Injectable({
  providedIn: 'root'
})
export class ModuleService {
  /* -----------------------------------------------------------------
     Config & caching
     ----------------------------------------------------------------- */
  private readonly gatewayBase = (environment.firewall || '').replace(/\/+$/, '');
  private readonly moduleUrl = `${this.gatewayBase}/api/agpt/module`;
  private readonly cacheTtlMs = 60_000; // 60 s

  private modulesSubject = new BehaviorSubject<ModuleInfo[]>([]);
  public modules$: Observable<ModuleInfo[]> = this.modulesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$ = this.errorSubject.asObservable();

  private initializedSubject = new BehaviorSubject<boolean>(false);
  public initialized$ = this.initializedSubject.asObservable();

  private lastFetchedAt = 0;
  private inFlightFetch: Observable<ModuleInfo[]> | null = null;

  /**
   * serverTime − deviceTime, in ms. Learned from every response
   * (`receivedRequestAt`), so a phone with a wrong clock / time zone / date still
   * builds the same daily key as the server.
   */
  private clockOffsetMs = 0;

  constructor(
    private http: HttpClient,
    private uzor: UzorService                // → provides hashSha256()
  ) {}

  /* -----------------------------------------------------------------
     1️⃣  Public bootstrap helpers – init / refresh / ensureLoaded
     ----------------------------------------------------------------- */
  init(force = false): Observable<ModuleInfo[]> {
    return this.fetchModules(force).pipe(
      tap(() => this.initializedSubject.next(true))
    );
  }

  /** Call when you just need the list but want the cache‑TTL to be honoured. */
  ensureLoaded(): Observable<ModuleInfo[]> {
    const stale = Date.now() - this.lastFetchedAt > this.cacheTtlMs;
    if (this.initializedSubject.value && !stale) {
      return of(this.modulesSubject.value);
    }
    return this.fetchModules(false);
  }

  /** Force a fresh fetch, ignoring the TTL. */
  refresh(): Observable<ModuleInfo[]> {
    return this.fetchModules(true);
  }

  /* -----------------------------------------------------------------
     2️⃣  Internal GET used by the three public bootstrap methods.
          – every request carries the custom `App` / `Ak` headers.
          – if the gateway rejects the key, the clock is re-synced from the
            response and the request is retried ONCE with the corrected key.
     ----------------------------------------------------------------- */
  private fetchModules(force: boolean): Observable<ModuleInfo[]> {
    const stale = Date.now() - this.lastFetchedAt > this.cacheTtlMs;

    // Return cached data if it is still fresh
    if (!force && !stale && this.initializedSubject.value) {
      return of(this.modulesSubject.value);
    }
    if (this.inFlightFetch) {
      return this.inFlightFetch;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    const appName = (environment.appName || '').trim(); // e.g. “Business”

    /** `defer` → headers (and therefore the daily key) are rebuilt on every attempt */
    const attempt = (): Observable<ModuleListResponse> =>
      defer(() =>
        this.http.get<ModuleListResponse>(this.moduleUrl, { headers: this.securityHeaders(appName) })
      );

    const request$ = attempt().pipe(
      catchError(err => {
        this.syncClock(err?.error);
        return this.isKeyRejection(err?.error) ? attempt() : throwError(() => err);
      }),
      tap(res => this.syncClock(res)),
      map(res => {
        if (!res.success) {
          throw new Error(res.error || 'Failed to fetch modules');
        }
        return res.modules ?? [];
      }),
      tap(mods => {
        this.modulesSubject.next(mods);
        this.lastFetchedAt = Date.now();
      }),
      catchError(err => {
        const msg = this.extractErrorMessage(err);
        this.errorSubject.next(msg);
        // keep the previous cache on error
        return of(this.modulesSubject.value);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
        this.inFlightFetch = null;
      })
    );

    this.inFlightFetch = request$;
    return request$;
  }

  /* -----------------------------------------------------------------
     3️⃣  Read‑only helpers (snapshot / lookup)
     ----------------------------------------------------------------- */
  getModulesSnapshot(): ModuleInfo[] {
    return this.modulesSubject.value;
  }

  getModule(appName: string): Observable<ModuleInfo | undefined> {
    return this.modules$.pipe(
      map(list => list.find(m => m.name.toLowerCase() === appName.toLowerCase()))
    );
  }

  getModuleSnapshot(appName: string): ModuleInfo | undefined {
    return this.modulesSubject.value.find(
      m => m.name.toLowerCase() === appName.toLowerCase()
    );
  }

  getPublicServices(appName: string): Observable<string[]> {
    return this.getModule(appName).pipe(map(m => m?.publicservices || []));
  }

  getServiceApiMap(appName: string): Observable<ServiceApiMap> {
    return this.getModule(appName).pipe(map(m => m?.api || {}));
  }

  getServiceRoutes(
    appName: string,
    serviceName: string
  ): Observable<ServiceRouteMap | undefined> {
    return this.getServiceApiMap(appName).pipe(map(api => api[serviceName]));
  }

  /* -----------------------------------------------------------------
     4️⃣  DAILY‑KEY generation (client side) – same algorithm as the
         back‑end (AppService.key()):

            SHA256( `${appName}${serverUrl}${YYYYMMDD}` )

         The date is the **UTC** day, measured on the SERVER's clock
         (device clock + the offset learned from responses). No more
         "my phone is already tomorrow" failures around midnight.
     ----------------------------------------------------------------- */

  /** "Now" according to the server. */
  private serverNow(): Date {
    return new Date(Date.now() + this.clockOffsetMs);
  }

  /** Learn the server↔device clock difference from any response envelope. */
  private syncClock(res: { receivedRequestAt?: string; _local?: boolean } | null | undefined): void {
    if (!res || res._local) return;          // synthetic error envelopes carry the DEVICE time – never sync from them
    const serverTime = res?.receivedRequestAt ? Date.parse(res.receivedRequestAt) : NaN;
    if (!Number.isNaN(serverTime)) {
      this.clockOffsetMs = serverTime - Date.now();
    }
  }

  /**
   * Did the gateway reject the request because of the daily key?
   * Adjust the two checks below to whatever your gateway actually returns
   * (best: have it send `code: 'KEY_INVALID'`).
   */
  private isKeyRejection(res: { success?: boolean; error?: string; code?: string } | null | undefined): boolean {
    if (!res || res.success === true) return false;
    if (res.code === 'KEY_INVALID' || res.code === 'KEY_EXPIRED') return true;
    const msg = typeof res.error === 'string' ? res.error : '';
    return /key/i.test(msg) && /invalid|expired|missing|mismatch|denied/i.test(msg);
  }

  /**
   * Build the key for the current server-side UTC day.
   * Returns a hash, or `null` if the pattern can't be built.
   */
  private _dailyKeyFor(appName: string): string | null {
    const now = this.serverNow();
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    const d = String(now.getUTCDate()).padStart(2, '0');
    const pattern = `${appName}https://${environment.serverAddress}:${environment.port}${y}${m}${d}`;
    return this.uzor.hashSha256(pattern);
  }

  /** `App` / `Ak` headers that must be present on every request to the gateway. */
  private securityHeaders(appName: string, base: HttpHeaders = new HttpHeaders()): HttpHeaders {
    const dailyKey = this._dailyKeyFor(appName);
    // HttpHeaders are immutable – each `set` returns a new instance.
    return dailyKey ? base.set('App', appName).set('Ak', dailyKey) : base;
  }

  /* -----------------------------------------------------------------
     5️⃣  CORE caller – adds the security headers, and transparently
         retries ONCE if the gateway says the key was wrong (the clock is
         re-synced from that very response first).
         The retry is safe: the key check happens before the service runs.
     ----------------------------------------------------------------- */
  call<T = any>(
    appName: string,
    serviceName: string,
    target: string,
    method: HttpVerb = 'get',
    body: any = null,
    params?: Record<string, string | number | boolean>
  ): Observable<ModuleCallResponse<T>> {
    const send = () => defer(() => this.dispatch<T>(appName, serviceName, target, method, body, params));

    return send().pipe(
      tap(res => this.syncClock(res)),
      mergeMap(res => (this.isKeyRejection(res) ? send().pipe(tap(r => this.syncClock(r))) : of(res)))
    );
  }

  /** One HTTP round-trip. Headers (incl. the daily key) are built fresh each time. */
  private dispatch<T>(
    appName: string,
    serviceName: string,
    target: string,
    method: HttpVerb,
    body: any,
    params?: Record<string, string | number | boolean>
  ): Observable<ModuleCallResponse<T>> {
    /* ---- internal proxy headers required by the gateway ---- */
    const headers = this.securityHeaders(
      appName,
      new HttpHeaders({
        _app: appName,
        _service: serviceName,
        _target: target
      })
    );

    /* ---- query‑string handling for GET / DELETE -------------- */
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        httpParams = httpParams.set(k, String(v));
      });
    }

    const options = {
      headers,
      params: httpParams,
      observe: 'body' as const
    };

    /* ---- dispatch according to the HTTP verb ---------------- */
    switch (method) {
      case 'get':
        return this.http
          .get<ModuleCallResponse<T>>(this.moduleUrl, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'delete':
        return this.http
          .delete<ModuleCallResponse<T>>(this.moduleUrl, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'post':
        return this.http
          .post<ModuleCallResponse<T>>(this.moduleUrl, body, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'put':
        return this.http
          .put<ModuleCallResponse<T>>(this.moduleUrl, body, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'patch':
        return this.http
          .patch<ModuleCallResponse<T>>(this.moduleUrl, body, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      default:
        return throwError(() => new Error(`Unsupported HTTP verb: ${method}`));
    }
  }

  /* -----------------------------------------------------------------
     Convenience wrappers – all funnel through `call()`
     ----------------------------------------------------------------- */
  get<T = any>(appName: string, serviceName: string, target: string, params?: Record<string, string | number | boolean>) {
    return this.call<T>(appName, serviceName, target, 'get', null, params);
  }
  post<T = any>(appName: string, serviceName: string, target: string, body: any) {
    return this.call<T>(appName, serviceName, target, 'post', body);
  }
  put<T = any>(appName: string, serviceName: string, target: string, body: any) {
    return this.call<T>(appName, serviceName, target, 'put', body);
  }
  patch<T = any>(appName: string, serviceName: string, target: string, body: any) {
    return this.call<T>(appName, serviceName, target, 'patch', body);
  }
  delete<T = any>(appName: string, serviceName: string, target: string, params?: Record<string, string | number | boolean>) {
    return this.call<T>(appName, serviceName, target, 'delete', null, params);
  }

  /* -----------------------------------------------------------------
     INTERNAL – normalise errors so callers always receive the
                 shared envelope shape.
     ----------------------------------------------------------------- */
  private normalizeCallError<T>(err: HttpErrorResponse | any): Observable<ModuleCallResponse<T>> {
    const body = err?.error;
    if (body && typeof body === 'object' && 'success' in body) {
      return of(body as ModuleCallResponse<T>);
    }
    return of({
      success: false,
      error: this.extractErrorMessage(err),
      receivedRequestAt: new Date().toISOString(),
      processingTimeMs: 0,
      data: null,
      _local: true
    });
  }

  private extractErrorMessage(err: HttpErrorResponse | Error | any): string {
    if (err instanceof HttpErrorResponse) {
      return err.error?.error || err.message || `HTTP ${err.status}`;
    }
    if (err instanceof Error) {
      return err.message;
    }
    return 'Unknown error';
  }
}
